/**
 * Modal shown by a button with a `modal` action. Renders admin-authored
 * rich-text content, sanitized with DOMPurify before injection (the server also
 * scrubs it on save — defense in depth, since this is shown to public viewers).
 * Width and title-bar colours are configurable per button.
 */

import React, { useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';

interface Props {
  title?: string;
  contentHtml: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  headerBg?: string;
  headerFg?: string;
  onClose: () => void;
  /** When given, rendered in the body INSTEAD of contentHtml (the map modal reuses this shell). */
  children?: React.ReactNode;
  bodyClassName?: string;
  /**
   * Bootstrap's `backdrop: 'static'` — clicking outside never closes. For bodies
   * the user drags inside (the map), where an accidental close costs them their
   * pan/zoom and their search.
   */
  staticBackdrop?: boolean;
}

const SIZE_CLASS: Record<string, string> = { sm: 'modal-sm', md: '', lg: 'modal-lg', xl: 'modal-xl' };

/** Relative luminance of a #rgb/#rrggbb colour (0 dark … 1 light). */
function luminance(hex?: string): number {
  if (!hex) return 1;
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length < 6) return 1;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export const ActionButtonModal: React.FC<Props> = ({ title, contentHtml, size = 'md', headerBg, headerFg, onClose, children, bodyClassName, staticBackdrop = false }) => {
  const { t } = useTranslation('results');
  // Default DOMPurify keeps class/style/id/data-* (author flexibility) while
  // stripping scripts / event handlers; ADD_ATTR keeps target on links.
  const clean = useMemo(
    () => DOMPurify.sanitize(contentHtml, { ADD_ATTR: ['target'] }),
    [contentHtml],
  );

  const pressedBackdrop = useRef(false);

  const headerStyle: React.CSSProperties = {};
  if (headerBg) headerStyle.background = headerBg;
  if (headerFg) headerStyle.color = headerFg;
  // Keep the close (×) visible: white variant when the title bar is dark.
  const closeWhite = headerBg ? luminance(headerBg) < 0.5 : false;

  // Portal onto <body>: the button bar lives inside the results grid, whose ancestors create
  // stacking contexts (sticky summary card, transformed visual card). A fixed-position modal
  // rendered in place gets trapped beneath them; on <body> it sits above everything.
  return createPortal(
    <div
      className="modal d-block fade show"
      tabIndex={-1}
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000 }}
      onMouseDown={(e) => { pressedBackdrop.current = e.target === e.currentTarget; }}
      onClick={(e) => {
        // A backdrop close must be a click that BEGAN on the backdrop. `click`
        // fires on the common ancestor of the mousedown and mouseup targets, so
        // a drag that starts inside the body and releases outside it reports the
        // backdrop as its target — closing the modal on what the user
        // experienced as a drag, not a click.
        const fromBackdrop = pressedBackdrop.current && e.target === e.currentTarget;
        pressedBackdrop.current = false;
        if (!staticBackdrop && fromBackdrop) onClose();
      }}
    >
      <div className={`modal-dialog modal-dialog-centered modal-dialog-scrollable ${SIZE_CLASS[size] || ''}`.trim()}>
        <div className="modal-content">
          <div className="modal-header" style={headerStyle}>
            <h5 className="modal-title fw-bold" style={headerFg ? { color: headerFg } : undefined}>{title || ''}</h5>
            <button type="button" className={`btn-close ${closeWhite ? 'btn-close-white' : ''}`.trim()} onClick={onClose} aria-label={t('auth:close')} />
          </div>
          {children !== undefined ? (
            <div className={`modal-body ${bodyClassName ?? ''}`.trim()}>{children}</div>
          ) : (
            // eslint-disable-next-line react/no-danger
            <div className={`modal-body ${bodyClassName ?? ''}`.trim()} dangerouslySetInnerHTML={{ __html: clean }} />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ActionButtonModal;
